import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { PusherEvent } from "Constants";
import { getUpcomingVideoMeetingsQuery } from "ChildrenPage/modules/ChildrenPageQuery";

const PUSHER_EVENTS = ["videoMeetingToastUpdate"];

const useUpcomingMeetings = ({
  userId,
  userEntityType,
  academicYearId,
  skip = false,
}) => {
  const { data, refetch } = useQuery(getUpcomingVideoMeetingsQuery, {
    fetchPolicy: "network-only",
    skip,
    variables: {
      id: userId,
      type: userEntityType,
      filters: {
        type: ["COURSE", "PRIMARY"],
        academicYearId,
        calendarIds: [],
        contentTypes: ["RECURRING_MEETING"],
        activeVideoMeetingToastFilter: true,
      },
      associatedEntitiesFilters: {
        entityTypes: ["GRADE", "COURSE"],
      },
    },
  });

  /**
   * Ably real-time refetch flow:
   *
   * 1. BE sends Ably messages (e.g. CALENDAR_EVENT_ITEM_TOAST_SHOW) to the user's channel
   * 2. PusherThunks.subscribeToChannel receives the message and looks up PUSHER_EVENTS_MAPPING
   *    in PusherUtils.js to resolve the Ably eventName to internal event names
   * 3. For unhandled events (default case in bindPusherEvent), it calls
   *    PusherEvent.emit(eventName) — PusherEvent is an EventEmitter instance
   *    exported from src/Constants/index.js
   * 4. This hook subscribes to the "videoMeetingToastUpdate" event via PusherEvent.on()
   *    and calls refetch() so the toast show/dismiss logic reacts to the change
   */
  useEffect(() => {
    // refetch() bypasses Apollo's `skip` flag, so we must also skip the
    // subscription itself — otherwise Pusher events would fire network calls
    // on routes where the query is intentionally paused.
    if (skip) return undefined;

    const handlePusherEvent = () => refetch();

    PUSHER_EVENTS.forEach(event => {
      PusherEvent.on(event, handlePusherEvent);
    });

    return () => {
      PUSHER_EVENTS.forEach(event => {
        PusherEvent.off(event, handlePusherEvent);
      });
    };
  }, [refetch, skip]);

  return {
    upcomingMeetingsData: data,
  };
};

export default useUpcomingMeetings;
